import React, { Component } from 'react';
import { Modal, Button, Form, Row, Col, Table, Alert, Badge } from 'react-bootstrap';
import Select from '../common/select';
import { HTTPService } from "../../services/httpService";
import ServicesAPI from "../../services/servicesApiV2";

const API = new HTTPService();

/**
 * VehicleSpecificPricing - Modal for managing vehicle-specific service pricing
 *
 * Can be opened from:
 * - Service Management (pre-filtered to a service)
 * - Vehicle Management (pre-filtered to a vehicle)
 *
 * Props:
 * - show: boolean - Whether modal is visible
 * - onHide: function - Called when modal is closed
 * - installerId: number - The installer ID
 * - preSelectedServiceId: number - Optional - Pre-select this service (from Service Management)
 * - preSelectedService: object - Optional - Pre-selected service data { id, service_name }
 * - preSelectedVehicle: object - Optional - Pre-select this vehicle (from Vehicle Management)
 *   { make, model, trimId, trimName, vehicleSize }
 * - onUpdate: function - Called when overrides are modified
 */
export class VehicleSpecificPricing extends Component {
  state = {
    // Mode: 'quick' for direct lookup, 'review' for list view
    mode: 'quick',

    // Vehicle selection
    makes: [],
    models: [],
    trims: [],
    selectedMake: 'Make',
    selectedMakeId: null,
    selectedModel: 'Model',
    selectedModelId: null,
    selectedTrim: 'Trim',
    selectedTrimId: null,
    vehicleSize: null,
    bodyStyle: null,

    // Service selection
    services: [],          // All master services for installer
    categories: [],        // Service categories
    selectedCategoryId: null,
    selectedServiceId: null,
    selectedServiceName: 'Service',

    // Pricing data
    basePrices: { retail: 0, wholesale: 0 },
    customPrices: { retail: '', wholesale: '' },
    hasExistingOverride: false,
    pricingRecords: [],    // installer_service records for selected service+size

    // Review mode data
    allOverrides: [],
    filterMake: '',
    filterModel: '',
    filterService: '',

    // UI state
    loading: false,
    saving: false,
    error: null,
    successMessage: null
  };

  async componentDidMount() {
    if (this.props.show) {
      await this.initialize();
    }
  }

  async componentDidUpdate(prevProps) {
    if (this.props.show && !prevProps.show) {
      await this.initialize();
    }
  }

  initialize = async () => {
    // Set default mode based on entry point:
    // - From vehicle page (preSelectedVehicle) → Review All
    // - From service page (preSelectedService) → Quick Override
    const defaultMode = this.props.preSelectedVehicle ? 'review' : 'quick';
    this.setState({ loading: true, error: null, mode: defaultMode });

    try {
      // Load makes and services in parallel
      await Promise.all([
        this.loadMakes(),
        this.loadServices()
      ]);

      // Track pre-selected values for pricing lookup
      let preSelectedServiceId = null;

      // Apply pre-selections if provided
      if (this.props.preSelectedService) {
        preSelectedServiceId = this.props.preSelectedService.id;
        await this.preselectService(this.props.preSelectedService);
      }

      if (this.props.preSelectedVehicle) {
        await this.preselectVehicle(this.props.preSelectedVehicle, preSelectedServiceId);
      }

      // If defaulting to review mode, load all overrides
      if (defaultMode === 'review') {
        await this.loadAllOverrides();
      }

    } catch (error) {
      console.error("Error initializing:", error);
      this.setState({ error: "Failed to load data. Please try again." });
    } finally {
      this.setState({ loading: false });
    }
  };

  // ============================================
  // DATA LOADING
  // ============================================

  loadMakes = async () => {
    try {
      const { data: makes } = await API.get("/makes");
      this.setState({ makes: makes || [] });
    } catch (error) {
      console.error("Error loading makes:", error);
    }
  };

  loadModels = async (makeId) => {
    try {
      const { data: models } = await API.get(`/models/0000${makeId}`);
      this.setState({ models: models || [] });
    } catch (error) {
      console.error("Error loading models:", error);
    }
  };

  loadTrims = async (modelId) => {
    try {
      const { data: trims } = await API.get(`/trims/${modelId}`);
      this.setState({ trims: trims || [] });
    } catch (error) {
      console.error("Error loading trims:", error);
    }
  };

  loadServices = async () => {
    try {
      // Load all master services for this installer
      const services = await ServicesAPI.getMasterServicesByInstaller(this.props.installerId);

      // Load categories
      const categories = await ServicesAPI.getServiceCategories();

      // Group services by category for the dropdown
      const categoriesById = {};
      categories.forEach(cat => {
        categoriesById[cat.category_id] = cat.category_name;
      });

      this.setState({
        services: services || [],
        categories: categories || [],
        categoriesById
      });
    } catch (error) {
      console.error("Error loading services:", error);
    }
  };

  loadPricingForSelection = async (overrideServiceId = null, overrideVehicleSize = null, overrideTrimId = null, overrideBodyStyle = null) => {
    // Use passed values or fall back to state
    const selectedServiceId = overrideServiceId || this.state.selectedServiceId;
    const bodyStyle = overrideBodyStyle || this.state.bodyStyle;
    const vehicleSize = overrideVehicleSize || this.state.vehicleSize;
    const trimId = overrideTrimId || this.state.selectedTrimId;

    if (!selectedServiceId || !vehicleSize) {
      this.setState({
        basePrices: { retail: 0, wholesale: 0 },
        customPrices: { retail: '', wholesale: '' },
        hasExistingOverride: false,
        pricingRecords: []
      });
      return;
    }

    try {
      // Get pricing records for this service and size — prefer bodyStyle for per-category resolution
      const filters = { masterServiceId: selectedServiceId };
      if (bodyStyle) {
        filters.bodyStyle = bodyStyle;
      } else {
        filters.vehicleSize = vehicleSize;
      }
      const pricingRecords = await ServicesAPI.getInstallerServices(
        this.props.installerId,
        filters
      );

      // Extract base prices - API may return price or service_cost
      let retailBase = 0;
      let wholesaleBase = 0;

      pricingRecords.forEach(record => {
        const price = record.price || record.service_cost || 0;
        if (record.catalog === 'RETAIL') {
          retailBase = price;
        } else if (record.catalog === 'WHOLE') {
          wholesaleBase = price;
        }
      });

      this.setState({
        basePrices: { retail: retailBase, wholesale: wholesaleBase },
        pricingRecords
      });

      // If we have a trim selected, check for existing overrides
      if (trimId) {
        await this.loadExistingOverrides(pricingRecords, trimId);
      }

    } catch (error) {
      console.error("Error loading pricing:", error);
    }
  };

  loadExistingOverrides = async (pricingRecords, overrideTrimId = null) => {
    const selectedTrimId = overrideTrimId || this.state.selectedTrimId;
    const records = pricingRecords || this.state.pricingRecords;

    if (!selectedTrimId || records.length === 0) return;

    try {
      let retailOverride = null;
      let wholesaleOverride = null;

      for (const record of records) {
        // API may return id or installer_service_id
        const recordId = record.id || record.installer_service_id;
        if (!recordId) continue;

        const overrides = await ServicesAPI.getTrimOverrides(recordId);
        const trimOverride = overrides.find(o => Number(o.id_car_trim) === Number(selectedTrimId));

        if (trimOverride) {
          if (record.catalog === 'RETAIL') {
            retailOverride = trimOverride.override_price;
          } else {
            wholesaleOverride = trimOverride.override_price;
          }
        }
      }

      const hasOverride = retailOverride !== null || wholesaleOverride !== null;

      this.setState({
        customPrices: {
          retail: retailOverride !== null ? retailOverride : '',
          wholesale: wholesaleOverride !== null ? wholesaleOverride : ''
        },
        hasExistingOverride: hasOverride
      });

    } catch (error) {
      console.error("Error loading existing overrides:", error);
    }
  };

  // Load all overrides for review mode - uses efficient single API call
  loadAllOverrides = async () => {
    this.setState({ loading: true });

    try {
      // Single API call to get all overrides for this installer
      const overrides = await ServicesAPI.getAllTrimOverrides(this.props.installerId);

      // Build a lookup from master_service_id to category name
      const serviceToCategory = {};
      (this.state.services || []).forEach(s => {
        const catName = this.state.categoriesById?.[s.category_id] || '';
        serviceToCategory[s.id] = catName;
      });

      // Transform API response to component format
      const allOverrides = overrides.map(o => ({
        serviceId: o.master_service_id,
        serviceName: o.service_name,
        categoryName: o.category_name || serviceToCategory[o.master_service_id] || '',
        trimId: o.id_car_trim,
        make: o.make_name,
        model: o.model_name,
        trim: o.trim_name,
        vehicleSize: o.vehicle_size,
        retailPrice: o.retail_price,
        retailInstallerServiceId: o.retail_installer_service_id,
        wholesalePrice: o.wholesale_price,
        wholesaleInstallerServiceId: o.wholesale_installer_service_id
      }));

      this.setState({ allOverrides, loading: false });

    } catch (error) {
      console.error("Error loading all overrides:", error);
      this.setState({ loading: false, error: "Failed to load overrides" });
    }
  };

  // ============================================
  // PRE-SELECTION
  // ============================================

  preselectService = async (service) => {
    this.setState({
      selectedServiceId: service.id,
      selectedServiceName: service.service_name
    });
    // Pricing will be loaded once a vehicle is selected
  };

  preselectVehicle = async (vehicle, preSelectedServiceId = null) => {
    // Set make, vehicle size, bodyStyle, and pre-filter review mode
    this.setState({
      selectedMake: vehicle.make,
      vehicleSize: vehicle.vehicleSize,
      bodyStyle: vehicle.bodyStyle || null,
      filterMake: vehicle.make,
      filterModel: vehicle.model
    });

    // Load models for this make and set model
    const makeData = this.state.makes.find(m => m.name === vehicle.make);
    if (makeData) {
      this.setState({ selectedMakeId: makeData.id_car_make });
      await this.loadModels(makeData.id_car_make);

      const modelData = this.state.models.find(m => m.name === vehicle.model);
      if (modelData) {
        this.setState({
          selectedModel: vehicle.model,
          selectedModelId: modelData.id_car_model
        });
        await this.loadTrims(modelData.id_car_model);

        // Set trim
        this.setState({
          selectedTrim: vehicle.trimName,
          selectedTrimId: vehicle.trimId
        });
      }
    }

    // Pass values directly since state updates are async
    const serviceId = preSelectedServiceId || this.state.selectedServiceId;
    await this.loadPricingForSelection(serviceId, vehicle.vehicleSize, vehicle.trimId, vehicle.bodyStyle);
  };

  // ============================================
  // EVENT HANDLERS
  // ============================================

  handleModeChange = async (newMode) => {
    this.setState({ mode: newMode });

    if (newMode === 'review' && this.state.allOverrides.length === 0) {
      await this.loadAllOverrides();
    }
  };

  handleMakeChange = async (event) => {
    const { value } = event.target;
    const makeData = this.state.makes.find(m => m.name === value);

    this.setState({
      selectedMake: value,
      selectedMakeId: makeData?.id_car_make || null,
      selectedModel: 'Model',
      selectedModelId: null,
      selectedTrim: 'Trim',
      selectedTrimId: null,
      vehicleSize: null,
      models: [],
      trims: [],
      customPrices: { retail: '', wholesale: '' },
      hasExistingOverride: false
    });

    if (makeData) {
      await this.loadModels(makeData.id_car_make);
    }
  };

  handleModelChange = async (event) => {
    const { value } = event.target;
    const modelData = this.state.models.find(m => m.name === value);
    // API may return vehicleSize, id_car_type, or vehicle_size
    const newVehicleSize = modelData?.vehicleSize || modelData?.id_car_type || modelData?.vehicle_size || null;

    this.setState({
      selectedModel: value,
      selectedModelId: modelData?.id_car_model || null,
      selectedTrim: 'Trim',
      selectedTrimId: null,
      vehicleSize: newVehicleSize,
      bodyStyle: null,
      trims: [],
      customPrices: { retail: '', wholesale: '' },
      hasExistingOverride: false
    });

    if (modelData) {
      await this.loadTrims(modelData.id_car_model);
      // Get bodyStyle from first trim for initial pricing lookup
      const firstTrim = this.state.trims[0];
      const newBodyStyle = firstTrim?.body_style || null;
      this.setState({ bodyStyle: newBodyStyle });
      // Load pricing - pass size and bodyStyle directly since state may not be updated yet
      await this.loadPricingForSelection(this.state.selectedServiceId, newVehicleSize, null, newBodyStyle);
    }
  };

  handleTrimChange = async (event) => {
    const { value } = event.target;
    const trimData = this.state.trims.find(t => t.name === value);

    this.setState({
      selectedTrim: value,
      selectedTrimId: trimData?.id_car_trim || null,
      bodyStyle: trimData?.body_style || this.state.bodyStyle
    });

    if (trimData) {
      // Load existing overrides for this trim
      await this.loadExistingOverrides();
    } else {
      this.setState({
        customPrices: { retail: '', wholesale: '' },
        hasExistingOverride: false
      });
    }
  };

  handleServiceChange = async (event) => {
    const { value } = event.target;
    const serviceData = this.state.services.find(s => s.service_name === value);
    const newServiceId = serviceData?.id || null;

    this.setState({
      selectedServiceId: newServiceId,
      selectedServiceName: value,
      customPrices: { retail: '', wholesale: '' },
      hasExistingOverride: false
    });

    if (serviceData) {
      // Pass service ID directly since state may not be updated yet
      await this.loadPricingForSelection(newServiceId, this.state.vehicleSize);
    }
  };

  handlePriceChange = (type, value) => {
    this.setState(prevState => ({
      customPrices: {
        ...prevState.customPrices,
        [type]: value
      }
    }));
  };

  handleSave = async () => {
    const { selectedServiceId, selectedTrimId, vehicleSize, customPrices } = this.state;

    if (!selectedServiceId || !selectedTrimId || !vehicleSize) {
      this.setState({ error: "Please select a vehicle and service" });
      return;
    }

    const retailPrice = customPrices.retail !== '' ? parseFloat(customPrices.retail) : null;
    const wholesalePrice = customPrices.wholesale !== '' ? parseFloat(customPrices.wholesale) : null;

    if (retailPrice === null && wholesalePrice === null) {
      this.setState({ error: "Please enter at least one custom price" });
      return;
    }

    this.setState({ saving: true, error: null });

    try {
      await ServicesAPI.setTrimOverrideForService(
        this.props.installerId,
        selectedServiceId,
        selectedTrimId,
        vehicleSize,
        retailPrice,
        wholesalePrice,
        this.state.bodyStyle
      );

      this.setState({
        saving: false,
        hasExistingOverride: true,
        successMessage: "Custom price saved successfully!"
      });

      // Clear success message after 3 seconds
      setTimeout(() => {
        this.setState({ successMessage: null });
      }, 3000);

      // Notify parent
      if (this.props.onUpdate) {
        this.props.onUpdate();
      }

    } catch (error) {
      console.error("Error saving override:", error);
      this.setState({
        saving: false,
        error: "Failed to save custom price. Please try again."
      });
    }
  };

  handleRemove = async () => {
    const { selectedServiceId, selectedTrimId, vehicleSize } = this.state;

    if (!selectedServiceId || !selectedTrimId || !vehicleSize) {
      return;
    }

    if (!window.confirm("Remove this custom price?")) {
      return;
    }

    this.setState({ saving: true, error: null });

    try {
      await ServicesAPI.removeTrimOverrideForService(
        this.props.installerId,
        selectedServiceId,
        selectedTrimId,
        vehicleSize,
        this.state.bodyStyle
      );

      this.setState({
        saving: false,
        hasExistingOverride: false,
        customPrices: { retail: '', wholesale: '' },
        successMessage: "Custom price removed successfully!"
      });

      // Clear success message after 3 seconds
      setTimeout(() => {
        this.setState({ successMessage: null });
      }, 3000);

      // Notify parent
      if (this.props.onUpdate) {
        this.props.onUpdate();
      }

    } catch (error) {
      console.error("Error removing override:", error);
      this.setState({
        saving: false,
        error: "Failed to remove custom price. Please try again."
      });
    }
  };

  handleClear = () => {
    this.setState({
      selectedMake: 'Make',
      selectedMakeId: null,
      selectedModel: 'Model',
      selectedModelId: null,
      selectedTrim: 'Trim',
      selectedTrimId: null,
      vehicleSize: null,
      bodyStyle: null,
      selectedServiceId: null,
      selectedServiceName: 'Service',
      models: [],
      trims: [],
      basePrices: { retail: 0, wholesale: 0 },
      customPrices: { retail: '', wholesale: '' },
      hasExistingOverride: false,
      pricingRecords: [],
      filterMake: '',
      filterModel: '',
      filterService: '',
      error: null,
      successMessage: null
    });
  };

  // ============================================
  // RENDER
  // ============================================

  render() {
    const { show, onHide } = this.props;
    const { mode, loading, error, successMessage } = this.state;

    return (
      <Modal show={show} onHide={onHide} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Vehicle-Specific Pricing</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {/* Mode Toggle */}
          <div className="text-center mb-4">
            <div className="btn-group" role="group">
              <button
                type="button"
                className={`btn ${mode === 'quick' ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => this.handleModeChange('quick')}
              >
                Quick Override
              </button>
              <button
                type="button"
                className={`btn ${mode === 'review' ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => this.handleModeChange('review')}
              >
                Review All
              </button>
            </div>
          </div>

          {error && <Alert variant="danger" className="py-2">{error}</Alert>}
          {successMessage && <Alert variant="success" className="py-2">{successMessage}</Alert>}

          {loading ? (
            <div className="text-center py-4">
              <i className="fa-sharp fa-regular fa-spinner fa-spin fa-2x"></i>
              <p className="mt-2">Loading...</p>
            </div>
          ) : (
            mode === 'quick' ? this.renderQuickMode() : this.renderReviewMode()
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }

  renderQuickMode() {
    const {
      makes, models, trims, services,
      selectedMake, selectedModel, selectedTrim, selectedServiceName,
      vehicleSize, basePrices, customPrices, hasExistingOverride,
      saving
    } = this.state;

    // Build dropdown options
    const makeList = ['Make', ...makes.map(m => m.name)];
    const modelList = ['Model', ...models.map(m => m.name)];
    const trimList = ['Trim', ...[...new Set(trims.map(t => t.name))]];
    const serviceList = ['Service', ...services.map(s => s.service_name)];

    const sizeLabel = { S: 'Small', M: 'Medium', L: 'Large', XL: 'X-Large' };
    const isReady = selectedMake !== 'Make' && selectedModel !== 'Model' &&
                    selectedTrim !== 'Trim' && selectedServiceName !== 'Service';

    return (
      <>
        {/* Vehicle Selection */}
        <div className="border-bottom pb-3 mb-3">
          <h6 className="text-muted mb-3">SELECT VEHICLE</h6>
          <Row>
            <Col md={4}>
              <Form.Group>
                <Select
                  name="make"
                  label=" "
                  options={makeList}
                  value={selectedMake}
                  onChange={this.handleMakeChange}
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Select
                  name="model"
                  label=" "
                  options={modelList}
                  value={selectedModel}
                  onChange={this.handleModelChange}
                  disabled={selectedMake === 'Make'}
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Select
                  name="trim"
                  label=" "
                  options={trimList}
                  value={selectedTrim}
                  onChange={this.handleTrimChange}
                  disabled={selectedModel === 'Model'}
                />
              </Form.Group>
            </Col>
          </Row>
          {vehicleSize && (
            <small className="text-muted">
              Vehicle Size: <Badge variant="secondary">{sizeLabel[vehicleSize] || vehicleSize}</Badge>
            </small>
          )}
        </div>

        {/* Service Selection */}
        <div className="border-bottom pb-3 mb-3">
          <h6 className="text-muted mb-3">SELECT SERVICE</h6>
          <Row>
            <Col md={8}>
              <Form.Group>
                <Select
                  name="service"
                  label=" "
                  options={serviceList}
                  value={selectedServiceName}
                  onChange={this.handleServiceChange}
                  disabled={!!this.props.preSelectedService}
                />
              </Form.Group>
            </Col>
            <Col md={4} className="d-flex align-items-center">
              {!this.props.preSelectedService && (
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={this.handleClear}
                >
                  Clear Selection
                </Button>
              )}
            </Col>
          </Row>
        </div>

        {/* Pricing Section */}
        {isReady && (
          <div>
            <h6 className="text-muted mb-3">
              PRICING
              {hasExistingOverride && (
                <Badge variant="warning" className="ml-2">Has Custom Price</Badge>
              )}
            </h6>

            <Row className="mb-3">
              <Col md={6}>
                <div className="text-center mb-2">
                  <strong>Retail</strong>
                </div>
                <div className="text-center text-muted mb-2">
                  Standard: ${basePrices.retail.toFixed(2)}
                </div>
                <Form.Group>
                  <div className="input-group">
                    <div className="input-group-prepend">
                      <span className="input-group-text">$</span>
                    </div>
                    <Form.Control
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="Custom price"
                      value={customPrices.retail}
                      onChange={(e) => this.handlePriceChange('retail', e.target.value)}
                    />
                  </div>
                </Form.Group>
              </Col>
              <Col md={6}>
                <div className="text-center mb-2">
                  <strong>Wholesale</strong>
                </div>
                <div className="text-center text-muted mb-2">
                  Standard: ${basePrices.wholesale.toFixed(2)}
                </div>
                <Form.Group>
                  <div className="input-group">
                    <div className="input-group-prepend">
                      <span className="input-group-text">$</span>
                    </div>
                    <Form.Control
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="Custom price"
                      value={customPrices.wholesale}
                      onChange={(e) => this.handlePriceChange('wholesale', e.target.value)}
                    />
                  </div>
                </Form.Group>
              </Col>
            </Row>

            <div className="text-center">
              <Button
                variant="primary"
                onClick={this.handleSave}
                disabled={saving}
                className="mr-2"
              >
                {saving ? 'Saving...' : 'Save Custom Price'}
              </Button>
              {hasExistingOverride && (
                <Button
                  variant="outline-danger"
                  onClick={this.handleRemove}
                  disabled={saving}
                >
                  Remove Custom Price
                </Button>
              )}
            </div>
          </div>
        )}

        {!isReady && (
          <div className="text-center text-muted py-4">
            Select a vehicle and service to set custom pricing
          </div>
        )}
      </>
    );
  }

  renderReviewMode() {
    const { allOverrides, filterMake, filterModel, filterService, loading } = this.state;

    // Apply filters
    let filtered = allOverrides;
    if (filterMake) {
      filtered = filtered.filter(o => o.make === filterMake);
    }
    if (filterModel) {
      filtered = filtered.filter(o => o.model === filterModel);
    }
    if (filterService) {
      filtered = filtered.filter(o =>
        o.serviceName?.toLowerCase().includes(filterService.toLowerCase())
      );
    }

    // Get unique makes for filter dropdown
    const uniqueMakes = [...new Set(allOverrides.map(o => o.make).filter(Boolean))].sort();

    // Get unique models filtered by selected make
    const modelsSource = filterMake ? allOverrides.filter(o => o.make === filterMake) : allOverrides;
    const uniqueModels = [...new Set(modelsSource.map(o => o.model).filter(Boolean))].sort();

    return (
      <>
        {/* Filters */}
        <Row className="mb-3">
          <Col md={3}>
            <Form.Group>
              <Form.Label className="small text-muted">Make</Form.Label>
              <Form.Control
                as="select"
                size="sm"
                value={filterMake}
                onChange={(e) => this.setState({ filterMake: e.target.value, filterModel: '' })}
              >
                <option value="">All Makes</option>
                {uniqueMakes.map(make => (
                  <option key={make} value={make}>{make}</option>
                ))}
              </Form.Control>
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group>
              <Form.Label className="small text-muted">Model</Form.Label>
              <Form.Control
                as="select"
                size="sm"
                value={filterModel}
                onChange={(e) => this.setState({ filterModel: e.target.value })}
                disabled={!filterMake}
              >
                <option value="">All Models</option>
                {uniqueModels.map(model => (
                  <option key={model} value={model}>{model}</option>
                ))}
              </Form.Control>
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group>
              <Form.Label className="small text-muted">Service</Form.Label>
              <Form.Control
                type="text"
                size="sm"
                placeholder="Search services..."
                value={filterService}
                onChange={(e) => this.setState({ filterService: e.target.value })}
              />
            </Form.Group>
          </Col>
          <Col md={3} className="d-flex align-items-end">
            <span className="text-muted mb-2" style={{ fontSize: '13px' }}>
              {filtered.length} of {allOverrides.length} custom prices
            </span>
          </Col>
        </Row>

        {/* Results Table */}
        {loading ? (
          <div className="text-center py-4">
            <i className="fa-sharp fa-regular fa-spinner fa-spin"></i> Loading overrides...
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center text-muted py-4">
            {allOverrides.length === 0
              ? "No custom prices set yet"
              : "No matches for current filters"
            }
          </div>
        ) : (
          <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
            <Table striped hover size="sm">
              <thead className="thead-light">
                <tr>
                  <th>Vehicle</th>
                  <th>Service</th>
                  <th className="text-right">Retail</th>
                  <th className="text-right">Wholesale</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((override, idx) => (
                  <tr
                    key={idx}
                    style={{ cursor: 'pointer' }}
                    onClick={() => this.selectOverrideForEdit(override)}
                  >
                    <td>
                      <strong>{override.make}</strong> {override.model}
                      <br />
                      <small className="text-muted">{override.trim}</small>
                    </td>
                    <td>{override.categoryName ? `${override.categoryName} - ` : ''}{override.serviceName}</td>
                    <td className="text-right">
                      {override.retailPrice ? `$${override.retailPrice.toFixed(2)}` : '-'}
                    </td>
                    <td className="text-right">
                      {override.wholesalePrice ? `$${override.wholesalePrice.toFixed(2)}` : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}

        <div className="text-center mt-3">
          <small className="text-muted">
            Click a row to edit, or switch to Quick Override mode to add new
          </small>
        </div>
      </>
    );
  }

  selectOverrideForEdit = async (override) => {
    // Load dependent dropdown data first
    const makeData = this.state.makes.find(m => m.name === override.make);
    if (!makeData) return;

    await this.loadModels(makeData.id_car_make);

    const modelData = this.state.models.find(m => m.name === override.model);
    const vehicleSize = modelData?.id_car_type || modelData?.vehicleSize || override.vehicleSize;

    if (modelData) {
      await this.loadTrims(modelData.id_car_model);
    }

    // Resolve bodyStyle from loaded trims or override data
    const trimMatch = this.state.trims.find(t => t.id_car_trim === override.trimId);
    const bodyStyle = trimMatch?.body_style || override.bodyStyle || null;

    // Pre-populate custom prices from the override row data we already have
    const hasRetail = override.retailPrice != null;
    const hasWholesale = override.wholesalePrice != null;

    // Set ALL selection state in one call to avoid timing issues
    this.setState({
      mode: 'quick',
      selectedMake: override.make,
      selectedMakeId: makeData.id_car_make,
      selectedModel: override.model,
      selectedModelId: modelData?.id_car_model || null,
      vehicleSize,
      bodyStyle,
      selectedTrim: override.trim,
      selectedTrimId: override.trimId,
      selectedServiceId: override.serviceId,
      selectedServiceName: override.serviceName,
      customPrices: {
        retail: hasRetail ? override.retailPrice : '',
        wholesale: hasWholesale ? override.wholesalePrice : ''
      },
      hasExistingOverride: hasRetail || hasWholesale
    });

    // Pass all values explicitly — don't rely on state reads
    await this.loadPricingForSelection(override.serviceId, vehicleSize, override.trimId, bodyStyle);
  };
}

export default VehicleSpecificPricing;
